import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { TruncationNotice } from "@/components/TruncationNotice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/chatbot/flagged/")({ component: FlaggedPage });

interface FlagRow {
  id: number;
  session_id: string;
  detector: "rule" | "retrieval";
  category: "prompt_injection" | "code_generation" | "abusive" | "off_topic";
  action: "refused" | "logged" | "throttled";
  rule_id: string | null;
  excerpt: string;
  top_score: number | null;
  reviewed_at: number | null;
  reviewed_by_name: string | null;
  ts: number;
}

const CATEGORY_LABEL: Record<FlagRow["category"], string> = {
  prompt_injection: "Prompt injection",
  code_generation: "Code generation",
  abusive: "Abusive",
  off_topic: "Off-topic",
};

/** Off-topic is informational (a knowledge-base gap), the rest are misuse. */
const CATEGORY_VARIANT: Record<FlagRow["category"], "destructive" | "secondary" | "outline"> = {
  prompt_injection: "destructive",
  abusive: "destructive",
  code_generation: "secondary",
  off_topic: "outline",
};

function FlaggedPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["chatbot", "flags"],
    // Guardrail flags accumulate with traffic, so bound the window.
    queryFn: () => fetchAll<FlagRow>("/admin/chatbot/flags", { maxRows: LOG_MAX_ROWS }),
  });

  const review = useMutation({
    mutationFn: (id: number) => api.post(`/admin/chatbot/flags/${id}/review`),
    onSuccess: () => {
      toast.success("Marked as reviewed");
      qc.invalidateQueries({ queryKey: ["chatbot", "flags"] });
    },
  });

  const columns: ColumnDef<FlagRow, unknown>[] = [
    {
      accessorKey: "ts", header: "When",
      cell: ({ row }) => <span className="font-mono text-xs">{format(row.original.ts * 1000, "MMM d, HH:mm")}</span>,
    },
    {
      accessorKey: "category", header: "Category",
      cell: ({ row }) => (
        <Badge variant={CATEGORY_VARIANT[row.original.category]}>{CATEGORY_LABEL[row.original.category]}</Badge>
      ),
      filterFn: (r, _i, v) => r.getValue("category") === v,
    },
    {
      accessorKey: "action", header: "Outcome",
      cell: ({ row }) => {
        const a = row.original.action;
        return (
          <Badge variant={a === "refused" ? "destructive" : a === "throttled" ? "secondary" : "outline"}>
            {a}
          </Badge>
        );
      },
      filterFn: (r, _i, v) => r.getValue("action") === v,
    },
    {
      accessorKey: "excerpt", header: "Message",
      cell: ({ row }) => (
        <p className="max-w-md line-clamp-2 text-sm" title={row.original.excerpt}>
          {row.original.excerpt}
        </p>
      ),
    },
    {
      id: "rule", header: "Signal",
      cell: ({ row }) => row.original.rule_id
        ? <span className="font-mono text-xs text-muted-foreground">{row.original.rule_id}</span>
        : row.original.top_score != null
          ? <span className="text-xs text-muted-foreground">score {row.original.top_score.toFixed(2)}</span>
          : <span className="text-muted-foreground">—</span>,
    },
    {
      id: "status", header: "Status",
      accessorFn: (r) => (r.reviewed_at ? "reviewed" : "new"),
      cell: ({ row }) => row.original.reviewed_at
        ? (
          <span className="text-xs text-muted-foreground">
            reviewed{row.original.reviewed_by_name ? ` by ${row.original.reviewed_by_name}` : ""}
          </span>
        )
        : <Badge variant="outline">new</Badge>,
      filterFn: (r, _i, v) => r.getValue("status") === v,
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Link
            to="/chatbot/conversations/$id" as never params={{ id: row.original.session_id }}
            className="text-sm text-primary hover:underline whitespace-nowrap"
          >
            <MessageSquare className="inline h-3.5 w-3.5" /> View chat
          </Link>
          {!row.original.reviewed_at && (
            <Button
              size="sm" variant="ghost"
              disabled={review.isPending}
              onClick={() => review.mutate(row.original.id)}
            >
              <Check className="h-3.5 w-3.5" /> Reviewed
            </Button>
          )}
        </div>
      ),
    },
  ];

  const items = data?.items ?? [];
  const unreviewed = items.filter((f) => !f.reviewed_at).length;

  return (
    <div className="space-y-2">
      <PageHeader
        title="Flagged messages"
        description={
          "Messages the chatbot guardrails caught — code-generation requests, jailbreak attempts and abuse are refused; " +
          "off-topic turns are only logged and still get a normal answer, so treat those as knowledge-base gaps."
        }
      />
      {data?.truncated && <TruncationNotice count={data.items.length} noun="flags" />}
      {unreviewed > 0 && (
        <p className="text-sm text-muted-foreground">
          {unreviewed} unreviewed {unreviewed === 1 ? "flag" : "flags"}.
        </p>
      )}
      <DataTable
        columns={columns}
        data={items}
        globalSearch
        globalSearchPlaceholder="Search message text…"
        pageSize={50}
        filters={[
          {
            id: "category", label: "Category",
            options: (Object.keys(CATEGORY_LABEL) as FlagRow["category"][]).map((v) => ({
              value: v, label: CATEGORY_LABEL[v],
            })),
          },
          {
            id: "action", label: "Outcome",
            options: [
              { value: "refused", label: "Refused" },
              { value: "logged", label: "Logged" },
              { value: "throttled", label: "Throttled" },
            ],
          },
          {
            id: "status", label: "Status",
            options: [
              { value: "new", label: "New" },
              { value: "reviewed", label: "Reviewed" },
            ],
          },
        ]}
        emptyMessage={isLoading ? "Loading…" : "Nothing flagged yet."}
      />
    </div>
  );
}
